import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  X,
  Loader2,
  FileText,
  Download,
  Send,
  Save,
  Eye,
  Lock,
  AlertTriangle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/components/ui/toaster.jsx';
import { cn } from '@/lib/utils';
import { fetchChargeConceptsByModo } from '../../../parametrizacion/charge-concepts/Services/chargeConceptsService.js';
import OfertaConceptosTable from './OfertaConceptosTable.jsx';
import EnviarOfertaDialog from './EnviarOfertaDialog.jsx';
import {
  getLclOferta,
  updateLclOferta,
  previewCalculoOferta,
  generarPdfOferta,
  fetchPreviewPdfUrl,
} from '../lclOfertas.service.js';
import { formatImporte } from '../../cotizaciones/utils/lclCotizacionCalc.js';

const MONEDAS = ['USD', 'COP', 'EUR', 'MXN'];

const inputCls =
  'h-9 w-full rounded-md border border-input bg-background px-3 text-sm ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

const soloFecha = (v) => (v ? String(v).slice(0, 10) : '');

const Campo = ({ label, children, hint }) => (
  <div className="space-y-1">
    <label className="text-xs font-medium text-muted-foreground">{label}</label>
    {children}
    {hint && <p className="text-[11px] text-muted-foreground">{hint}</p>}
  </div>
);

/**
 * Editor de la oferta al cliente.
 *
 * Dos pestañas: los datos y conceptos por un lado, la previsualización
 * del PDF por el otro. La previsualización pide el PDF al servidor, así
 * que muestra exactamente lo que va a recibir el cliente — incluido el
 * hecho de que los costos del proveedor no aparecen.
 *
 * Una oferta ya enviada se abre en solo lectura: para cambiarla hay que
 * emitir una versión nueva, o el PDF que tiene el cliente dejaría de
 * coincidir con lo guardado.
 */
export default function LclOfertaDialog({ open, onOpenChange, ofertaId, onChanged }) {
  const toast = useToast();
  const [oferta, setOferta] = useState(null);
  const [calculo, setCalculo] = useState(null);
  const [catalogo, setCatalogo] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [generando, setGenerando] = useState(false);
  const [tab, setTab] = useState('datos');
  const [enviarAbierto, setEnviarAbierto] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewCargando, setPreviewCargando] = useState(false);
  const [previewError, setPreviewError] = useState(null);
  const debounceRef = useRef(null);
  const previewUrlRef = useRef(null);

  const soloLectura = !!oferta && oferta.estado !== 'Borrador';

  const cargar = useCallback(async () => {
    if (!ofertaId) return;
    setCargando(true);
    try {
      const r = await getLclOferta(ofertaId);
      if (r?.success) setOferta(r.data);
      else toast.error('No se pudo cargar la oferta', r?.message);
    } catch (e) {
      toast.error('No se pudo cargar la oferta', e?.message);
    } finally {
      setCargando(false);
    }
  }, [ofertaId, toast]);

  useEffect(() => {
    if (open) {
      cargar();
      setTab('datos');
    }
  }, [open, cargar]);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const r = await fetchChargeConceptsByModo('lcl');
        if (r?.success) setCatalogo(r.data || []);
      } catch {
        /* sin catálogo el combobox queda vacío */
      }
    })();
  }, [open]);

  /**
   * Recalcula en el servidor con debounce.
   *
   * Se muestran los totales del servidor y no un cálculo local: es la
   * forma de garantizar que lo que se ve editando es lo que se guarda.
   */
  useEffect(() => {
    if (!oferta) return;
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const r = await previewCalculoOferta({
          carga: oferta.carga,
          valorMercancia: oferta.valorMercancia,
          monedaBase: oferta.monedaBase,
          monedaSecundaria: oferta.monedaSecundaria,
          trm: oferta.trm,
          conceptos: (oferta.conceptos || []).map(limpiarConcepto),
        });
        if (r?.success) setCalculo(r.data);
      } catch {
        /* si falla el preview, se sigue editando; el guardado recalcula */
      }
    }, 450);
    return () => clearTimeout(debounceRef.current);
  }, [oferta]);

  /**
   * Pide el PDF y lo deja como object URL. Se hace acá y no con la URL
   * directa porque el iframe no manda el header del tenant.
   */
  const cargarPreview = useCallback(async () => {
    if (!ofertaId) return;
    setPreviewCargando(true);
    setPreviewError(null);
    try {
      const url = await fetchPreviewPdfUrl(ofertaId);
      if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = url;
      setPreviewUrl(url);
    } catch (e) {
      setPreviewError(e?.message || 'No se pudo generar la vista previa');
    } finally {
      setPreviewCargando(false);
    }
  }, [ofertaId]);

  // El blob queda en memoria hasta que se libera.
  useEffect(
    () => () => {
      if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
    },
    [],
  );

  const set = (campo, valor) => setOferta((p) => ({ ...p, [campo]: valor }));

  const guardar = async () => {
    setGuardando(true);
    try {
      const r = await updateLclOferta(ofertaId, {
        numero: oferta.numero,
        fechaEmision: oferta.fechaEmision,
        vigenciaHasta: oferta.vigenciaHasta,
        contactoSnapshot: oferta.contactoSnapshot,
        productoDescripcion: oferta.productoDescripcion,
        htsCode: oferta.htsCode,
        frecuencia: oferta.frecuencia,
        tiempoTransito: oferta.tiempoTransito,
        diasTransito: oferta.diasTransito,
        monedaBase: oferta.monedaBase,
        monedaSecundaria: oferta.monedaSecundaria,
        trm: oferta.trm,
        conceptos: (oferta.conceptos || []).map(limpiarConcepto),
        notas: oferta.notas,
        terminosCondiciones: oferta.terminosCondiciones,
      });
      if (r?.success) {
        setOferta(r.data);
        toast.success('Oferta guardada', r.data?.numero);
        onChanged?.();
        // El PDF se rearma con lo recién guardado.
        if (tab === 'preview') cargarPreview();
      } else {
        toast.error('No se pudo guardar', [].concat(r?.message || '').join(' · '));
      }
    } catch (e) {
      toast.error('No se pudo guardar', e?.message);
    } finally {
      setGuardando(false);
    }
  };

  const descargar = async () => {
    setGenerando(true);
    try {
      const r = await generarPdfOferta(ofertaId);
      if (r?.success && r.data?.url) {
        window.open(r.data.url, '_blank', 'noopener');
        toast.success('PDF generado', r.data.documento?.nombreArchivo);
        cargar();
        onChanged?.();
      } else {
        toast.error('No se pudo generar el PDF', r?.message);
      }
    } finally {
      setGenerando(false);
    }
  };

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onOpenChange?.(false)}>
      {/*
        Usa el `Dialog` del proyecto y no un overlay propio.

        Este modal se abre desde adentro del detalle de la solicitud, que ya
        es un `DialogContent` de Radix y lleva
        `translate-x-[-50%] translate-y-[-50%]`. Un ancestro con `transform`
        pasa a ser el bloque contenedor de los `position: fixed` de adentro:
        `inset-0` dejaba de medir contra la pantalla y medía contra esa
        tarjeta, mientras las unidades `vh` seguían midiendo contra la
        pantalla. El modal terminaba más alto que su propio contenedor y el
        `overflow-hidden` del padre le cortaba el encabezado y los botones.

        Portearlo a mano a `<body>` no alcanza: Radix deja el `body` en
        `pointer-events: none` y devuelve el foco a su contenedor cuando
        aterriza afuera, así que quedaría sin clicks y sin poder escribir.
        El `Dialog` anidado sí funciona porque Radix apila foco y clicks
        entre diálogos, igual que el `AlertDialog` de borrar.

        Alto definido (no `max-h`) y columna flex: encabezado, pestañas y pie
        quedan fijos y sólo scrollea el cuerpo. El alto definido es además lo
        que deja estirarse al iframe de la vista previa; contra un padre de
        alto automático un `h-full` colapsa a cero.
      */}
      <DialogContent
        hideClose
        className="flex h-[calc(100vh-2rem)] w-[95vw] max-w-none flex-col gap-0 overflow-hidden p-0"
      >
        {/* Encabezado */}
        <div className="flex shrink-0 items-start justify-between border-b p-4">
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="text-lg font-semibold">
                {oferta?.numero || 'Oferta'}{' '}
                <span className="text-sm font-normal text-muted-foreground">
                  v{oferta?.version || 1}
                </span>
              </h3>
              {oferta && (
                <Badge variant="outline" className="font-normal">
                  {oferta.estado}
                </Badge>
              )}
              {soloLectura && (
                <Badge
                  variant="outline"
                  className="border-amber-300 bg-amber-50 font-normal text-amber-800"
                >
                  <Lock className="mr-1 h-3 w-3" />
                  solo lectura · creá una versión nueva para cambiarla
                </Badge>
              )}
            </div>
            <p className="mt-0.5 text-sm text-muted-foreground">
              {oferta?.clienteSnapshot?.nombre}
              {oferta?.origen?.ciudad
                ? ` · ${oferta.origen.ciudad} → ${oferta.destino?.ciudad || ''}`
                : ''}
            </p>
          </div>
          <Button variant="ghost" size="icon" onClick={() => onOpenChange?.(false)}>
            <X className="h-5 w-5" />
          </Button>
        </div>

        {/* Pestañas */}
        <div className="flex shrink-0 gap-1 border-b px-4">
          {[
            { id: 'datos', label: 'Datos y conceptos', icon: FileText },
            { id: 'preview', label: 'Vista previa del PDF', icon: Eye },
          ].map((t) => {
            const I = t.icon;
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => {
                  setTab(t.id);
                  if (t.id === 'preview') cargarPreview();
                }}
                className={cn(
                  'flex items-center gap-1.5 border-b-2 px-3 py-2 text-sm transition-colors',
                  tab === t.id
                    ? 'border-primary font-medium text-primary'
                    : 'border-transparent text-muted-foreground hover:text-foreground',
                )}
              >
                <I className="h-4 w-4" /> {t.label}
              </button>
            );
          })}
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto p-4">
          {cargando && (
            <div className="flex items-center justify-center py-16 text-muted-foreground">
              <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Cargando…
            </div>
          )}

          {!cargando && oferta && tab === 'datos' && (
            <div className="space-y-6">
              <section className="grid grid-cols-1 gap-3 md:grid-cols-4">
                <Campo label="Número">
                  <input
                    className={inputCls}
                    value={oferta.numero || ''}
                    readOnly={soloLectura}
                    onChange={(e) => set('numero', e.target.value)}
                  />
                </Campo>
                <Campo label="Fecha de emisión">
                  <input
                    type="date"
                    className={inputCls}
                    value={soloFecha(oferta.fechaEmision)}
                    readOnly={soloLectura}
                    onChange={(e) => set('fechaEmision', e.target.value || null)}
                  />
                </Campo>
                <Campo label="Vigencia hasta">
                  <input
                    type="date"
                    className={inputCls}
                    value={soloFecha(oferta.vigenciaHasta)}
                    readOnly={soloLectura}
                    onChange={(e) => set('vigenciaHasta', e.target.value || null)}
                  />
                </Campo>
                <Campo label="ATN (contacto del cliente)" hint="Va en la cabecera del PDF">
                  <input
                    className={inputCls}
                    value={oferta.contactoSnapshot?.nombre || ''}
                    readOnly={soloLectura}
                    onChange={(e) =>
                      set('contactoSnapshot', {
                        ...(oferta.contactoSnapshot || {}),
                        nombre: e.target.value,
                      })
                    }
                  />
                </Campo>
              </section>

              <section className="grid grid-cols-1 gap-3 md:grid-cols-4">
                <Campo label="Producto">
                  <input
                    className={inputCls}
                    value={oferta.productoDescripcion || ''}
                    readOnly={soloLectura}
                    onChange={(e) => set('productoDescripcion', e.target.value)}
                  />
                </Campo>
                <Campo label="Partida (HTS)">
                  <input
                    className={inputCls}
                    value={oferta.htsCode || ''}
                    readOnly={soloLectura}
                    onChange={(e) => set('htsCode', e.target.value)}
                  />
                </Campo>
                <Campo label="Frecuencia">
                  <input
                    className={inputCls}
                    value={oferta.frecuencia || ''}
                    readOnly={soloLectura}
                    onChange={(e) => set('frecuencia', e.target.value)}
                  />
                </Campo>
                <Campo label="Tiempo de tránsito">
                  <input
                    className={inputCls}
                    value={oferta.tiempoTransito || ''}
                    readOnly={soloLectura}
                    onChange={(e) => set('tiempoTransito', e.target.value)}
                  />
                </Campo>
              </section>

              <section className="grid grid-cols-1 gap-3 md:grid-cols-3">
                <Campo label="Moneda de la oferta">
                  <select
                    className={inputCls}
                    value={oferta.monedaBase || 'USD'}
                    disabled={soloLectura}
                    onChange={(e) => set('monedaBase', e.target.value)}
                  >
                    {MONEDAS.map((m) => (
                      <option key={m} value={m}>
                        {m}
                      </option>
                    ))}
                  </select>
                </Campo>
                <Campo
                  label="Segunda columna de moneda"
                  hint="Como en el formato con USD y COP"
                >
                  <select
                    className={inputCls}
                    value={oferta.monedaSecundaria || ''}
                    disabled={soloLectura}
                    onChange={(e) => set('monedaSecundaria', e.target.value || null)}
                  >
                    <option value="">— ninguna —</option>
                    {MONEDAS.filter((m) => m !== (oferta.monedaBase || 'USD')).map((m) => (
                      <option key={m} value={m}>
                        {m}
                      </option>
                    ))}
                  </select>
                </Campo>
                <Campo label={`TRM (${oferta.monedaSecundaria || '—'} por 1 ${oferta.monedaBase || 'USD'})`}>
                  <input
                    type="number"
                    step="any"
                    className={inputCls}
                    value={oferta.trm ?? ''}
                    placeholder="3219.31"
                    readOnly={soloLectura || !oferta.monedaSecundaria}
                    onChange={(e) =>
                      set('trm', e.target.value === '' ? null : Number(e.target.value))
                    }
                  />
                </Campo>
              </section>

              {calculo?.totalIncompleto && (
                <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                  <span>
                    Hay conceptos en {(calculo.monedasSinTasa || []).join(', ')} sin tasa
                    de cambio: no entran en el total. Cargá la TRM.
                  </span>
                </div>
              )}

              <section className="space-y-3">
                <h4 className="text-sm font-semibold">Conceptos</h4>
                <OfertaConceptosTable
                  conceptos={oferta.conceptos || []}
                  onChange={(cs) => set('conceptos', cs)}
                  calculo={calculo}
                  monedaBase={oferta.monedaBase || 'USD'}
                  monedaSecundaria={oferta.monedaSecundaria}
                  catalogo={catalogo}
                  readOnly={soloLectura}
                />
              </section>

              <section className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <Campo label="Notas" hint="Bloque NOTAS del PDF">
                  <textarea
                    rows={5}
                    className={cn(inputCls, 'h-auto py-2')}
                    value={oferta.notas || ''}
                    readOnly={soloLectura}
                    onChange={(e) => set('notas', e.target.value)}
                  />
                </Campo>
                <Campo label="Términos y condiciones">
                  <textarea
                    rows={5}
                    className={cn(inputCls, 'h-auto py-2')}
                    value={oferta.terminosCondiciones || ''}
                    readOnly={soloLectura}
                    onChange={(e) => set('terminosCondiciones', e.target.value)}
                  />
                </Campo>
              </section>
            </div>
          )}

          {!cargando && oferta && tab === 'preview' && (
            <div className="flex h-full flex-col gap-2">
              <p className="flex shrink-0 items-center gap-1.5 text-xs text-muted-foreground">
                <Lock className="h-3 w-3" />
                Esto es exactamente lo que recibe el cliente. Los costos del proveedor y
                tu margen no aparecen.
              </p>
              {previewCargando && (
                <div className="flex min-h-0 flex-1 items-center justify-center rounded border bg-muted text-muted-foreground">
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Generando el PDF…
                </div>
              )}
              {!previewCargando && previewError && (
                <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-2 rounded border bg-muted text-sm text-rose-700">
                  <AlertTriangle className="h-6 w-6" />
                  {previewError}
                  <Button size="sm" variant="outline" onClick={cargarPreview}>
                    Reintentar
                  </Button>
                </div>
              )}
              {!previewCargando && !previewError && previewUrl && (
                <iframe
                  title="Vista previa de la oferta"
                  src={previewUrl}
                  className="min-h-0 w-full flex-1 rounded border bg-muted"
                />
              )}
            </div>
          )}
        </div>

        {/* Pie */}
        <div className="flex shrink-0 flex-wrap items-center justify-between gap-3 border-t p-4">
          <div className="text-sm">
            {calculo && (
              <>
                <span className="text-muted-foreground">Total </span>
                <span className="text-lg font-semibold">
                  {formatImporte(calculo.totalBase, oferta?.monedaBase)}
                </span>
                {oferta?.monedaSecundaria && calculo.totalSecundaria != null && (
                  <span className="ml-2 text-muted-foreground">
                    / {formatImporte(calculo.totalSecundaria, oferta.monedaSecundaria)}
                  </span>
                )}
              </>
            )}
          </div>

          <div className="flex flex-wrap gap-2">
            {!soloLectura && (
              <Button onClick={guardar} disabled={guardando}>
                {guardando ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Save className="mr-2 h-4 w-4" />
                )}
                Guardar
              </Button>
            )}
            <Button variant="outline" onClick={descargar} disabled={generando}>
              {generando ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Download className="mr-2 h-4 w-4" />
              )}
              Descargar PDF
            </Button>
            <Button variant="outline" onClick={() => setEnviarAbierto(true)}>
              <Send className="mr-2 h-4 w-4" /> Enviar al cliente
            </Button>
          </div>
        </div>
      </DialogContent>

      {enviarAbierto && (
        <EnviarOfertaDialog
          open
          onOpenChange={(v) => !v && setEnviarAbierto(false)}
          oferta={oferta}
          onSent={() => {
            cargar();
            onChanged?.();
          }}
        />
      )}
    </Dialog>
  );
}

/**
 * Deja el concepto con los campos que acepta el DTO.
 *
 * Los totales los recalcula el servidor, pero se mandan igual porque el
 * DTO los acepta; lo que sí hay que quitar son los `_id` de Mongo de los
 * subdocumentos, que el ValidationPipe rechaza.
 */
function limpiarConcepto(c) {
  const { _id, ...resto } = c || {};
  if (resto.costoProveedor) {
    const { _id: __, ...cp } = resto.costoProveedor;
    resto.costoProveedor = cp;
  }
  return resto;
}
